import { t } from 'i18next';
import { ErrorNotification, InfoNotification, Notification, SuccessNotification, WarningNotification } from '../../../common/utils/notifications/Notification';
import { JiraError } from './valueObjects/JiraError';
import { JiraIssue } from './valueObjects/JiraIssue';

export enum JiraIssueStatus {
  SUCCESS,
  PARTIAL_SUCCESS,
  ERROR,
  EMPTY,
  EMPTY_WITH_EXCEPTIONS,
}

export class JiraExportResponse {
  constructor(private readonly issues: JiraIssue[], private readonly errors: JiraError[], private readonly hasAnyInstance: boolean) {}

  getIssues() {
    return this.issues;
  }

  getErrors() {
    return this.errors;
  }

  getStatus() {
    if (this.issues.length === 0 && this.errors.length === 0 && !this.hasAnyInstance) {
      return JiraIssueStatus.EMPTY;
    }
    if (this.issues.length === 0 && this.errors.length === 0 && this.hasAnyInstance) {
      return JiraIssueStatus.EMPTY_WITH_EXCEPTIONS;
    }
    if (this.issues.length === 0 && this.errors.length > 0) {
      return JiraIssueStatus.ERROR;
    }
    if (this.issues.length > 0 && this.errors.length > 0) {
      return JiraIssueStatus.PARTIAL_SUCCESS;
    }
    return JiraIssueStatus.SUCCESS;
  }

  getMessage(): Notification {
    const status = this.getStatus();
    switch (status) {
      case JiraIssueStatus.SUCCESS:
        return new SuccessNotification(t('jiraExportSuccess', { count: this.issues.length }));
      case JiraIssueStatus.PARTIAL_SUCCESS:
        return new WarningNotification(t('jiraExportPartial', { success: this.issues.length, failed: this.errors.length, error: this.getFirstErrorType() }));
      case JiraIssueStatus.EMPTY:
        return new InfoNotification(t('jiraExportZero'));
      case JiraIssueStatus.EMPTY_WITH_EXCEPTIONS:
        return new InfoNotification(t('jiraExportZeroExceptionsAvailable'));
      case JiraIssueStatus.ERROR:
        return new ErrorNotification(t('jiraExportFailure', { error: this.getFirstErrorType() }));
    }
  }

  private getFirstErrorType() {
    const error = this.errors[0]?.getErrors()[0];
    if (!error) {
      return t('jiraExportErrors.generic');
    }
    if (error.getField() === 'project') {
      return t('jiraExportErrors.project', { value: error.getValue() });
    }
    if (error.getField() === 'issuetype') {
      return t('jiraExportErrors.issueType', { value: error.getValue() });
    }
    if (error.getField() === 'priority') {
      return t('jiraExportErrors.priority', { value: error.getValue() });
    }
    return t('jiraExportErrors.generic');
  }
}
